import { trpc } from "./trpc.js";
import { getCurator, type CuratorId, type CuratorPersona } from "./curator.js";
import { getTelegram } from "./lib/telegram.js";

const LOCAL_KEY = "companion:curator-id";
const CURATORS: readonly CuratorId[] = ["mark", "yulia", "lev"] as const;

/**
 * Закрепляет за юзером одного из трёх редакторов (Марк / Юля / Лев) и возвращает
 * его персону. Приоритет:
 *   1. backend (`me.get.user.curatorId`) — источник истины
 *   2. detereministic hash от Telegram user id → один и тот же редактор
 *      при любом перезаходе, честное распределение 1/3
 *   3. localStorage (для браузер-тестов без Telegram)
 *   4. mark по дефолту (никогда не должно случаться в TG)
 */
export function useCurator(): CuratorPersona {
  const { data } = trpc.me.get.useQuery(undefined, { staleTime: 60_000 });
  const fromBackend = data?.user.curatorId;
  if (fromBackend) return getCurator(fromBackend);
  return getCurator(resolveCuratorIdClientSide());
}

function resolveCuratorIdClientSide(): CuratorId {
  // 1. Детерминированно — hash от Telegram user id (стабильно между сессиями).
  const tg = getTelegram();
  const tgUserId = tg?.initDataUnsafe.user?.id;
  if (typeof tgUserId === "number") {
    const chosen = pickByHash(String(tgUserId));
    // Кешируем, чтобы и без TG (например, после navigation без реинита) быстро отдать.
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LOCAL_KEY, chosen);
      }
    } catch {
      /* privacy mode */
    }
    return chosen;
  }

  // 2. Браузер без Telegram — читаем ранее сохранённое или один раз случайно.
  if (typeof window === "undefined") return "mark";
  const saved = window.localStorage.getItem(LOCAL_KEY);
  if (isCuratorId(saved)) return saved;
  const chosen = CURATORS[Math.floor(Math.random() * CURATORS.length)] ?? "mark";
  try {
    window.localStorage.setItem(LOCAL_KEY, chosen);
  } catch {
    /* privacy mode */
  }
  return chosen;
}

function pickByHash(seed: string): CuratorId {
  // Простой djb2-подобный хеш — детерминированный и равномерный на больших
  // множествах tg id, не требует зависимостей.
  let h = 5381;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0;
  }
  return CURATORS[h % CURATORS.length] ?? "mark";
}

function isCuratorId(v: unknown): v is CuratorId {
  return v === "mark" || v === "yulia" || v === "lev";
}

/** Для отладки / сброса демо. Не используется в UI. */
export function resetAssignedCurator(): void {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(LOCAL_KEY);
  }
}
